import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON, V4_ICON_BUTTON_SMALL } from '../../../../theme/v4Compat';
import { useNavigate } from 'react-router-dom';
import { DeleteOutlined as DeleteOutline, Notifications, Refresh } from '@mui/icons-material';
import NotificationRulesService from '../../../../hooks/NotificationRulesService';
import {
  DayOfWeek,
  LungWeatherRuleDataViewModel,
  MinRegAnimalsRuleData,
  NotificationRuleViewModel,
  NumRuleDataViewModel,
  PercentageRuleData,
  RuleType,
  ValidationPeriodType
} from '../../../../models/notification-rule';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import { AnimalType } from '../../../../models/animal';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { notifications } from '../../../../utils';
import EmailPreview from '../EmailPreview/EmailPreview';
import { useConfirmation } from '../../../../hooks/ConfirmationService';
import NotificationRuleDialog from '../NotificationRuleDialog';
import { Add } from '@mui/icons-material';
import { useFarmDateTime } from '../../../../hooks/useFarmDateTime';
import useFeatures, { FeatureType } from '../../../../hooks/features';

interface IProps {}

const notificationRulesService = new NotificationRulesService();

let t: TFunction;

const NotificationRulesList: React.FC<{ props?: IProps }> = (_props) => {
  t = useTranslation().t;

  // variables
  const styles = sxStyles(useTheme());
  const confirmDialog = useConfirmation();
  const { formatDate } = useFarmDateTime();
  const navigate = useNavigate();
  const { hasFeature } = useFeatures();

  // Off means the correlation rule does not exist as far as the UI is concerned: no section, no cards and no
  // way into the review page. Rules already stored keep working on the backend.
  const lungWeatherEnabled = hasFeature(FeatureType.USE_LUNG_WEATHER_RULE);

  const ruleTypes = [
    RuleType.PercentageLimit,
    RuleType.MinRegistered,
    RuleType.NumLimit,
    ...(lungWeatherEnabled ? [RuleType.LungWeatherCorrelation] : [])
  ];

  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [notificationRules, setNotificationRules] = useState<NotificationRuleViewModel[]>([]);

  // functions
  const getNotifications = (page: number, size: number) => {
    setIsLoading(true);

    notificationRulesService
      .getRules(page, size)
      .then((n) => {
        setTotal(n.total);
        setSize(n.size);
        setPage(n.page);
        setTotalPages(total < size ? 1 : Math.ceil(total / size));
        setNotificationRules(n.data);
      })
      .finally(() => setIsLoading(false));
  };

  const deleteRule = (rule: NotificationRuleViewModel) => {
    confirmDialog({
      dialogId: 'delete-rule-dialog',
      title: t('f-supp-rule-del-title'),
      message: t('f-supp-rule-del-msg'),
      alertSeverity: 'warning'
    })
      .then(() => {
        notificationRulesService
          .deleteRule(rule.id)
          .then((_) => getNotifications(page, size))
          .catch((e) => notifications.error(e.toString()));
      })
      .catch(() => {});
  };

  // lifecycle
  useEffect(() => {
    getNotifications(page, size);
  }, []);

  const [notificationRuleDialogOpen, setNotificationRuleDialogOpen] = React.useState(false);
  const [dialogMode, setRuleDialogMode] = React.useState<'create' | 'update'>('create');
  const [selectedRule, setSelectedRule] = React.useState<NotificationRuleViewModel | null>(null);

  const handleUpdate = (rule: NotificationRuleViewModel) => {
    setRuleDialogMode('update');
    setSelectedRule(rule);
    setNotificationRuleDialogOpen(true);
  };

  const handleCreate = () => {
    setRuleDialogMode('create');
    setSelectedRule(null);
    setNotificationRuleDialogOpen(true);
  };

  // html
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Grid container spacing={1}>
            <Notifications color="primary" />
            <Box sx={{ ml: 1 }}>
              <Typography variant="h6" color="primary">
                {t('f-notification-rules')}
              </Typography>
            </Box>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleCreate} sx={V4_ICON_BUTTON}>
            <Add color="primary" />
          </IconButton>
          <IconButton color="primary" onClick={() => getNotifications(page, size)} sx={V4_ICON_BUTTON}>
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {!isLoading ? (
          <Grid container direction="column" spacing={4}>
            {ruleTypes.map((ruleType) => (
              <Grid item container key={ruleType}>
                <Grid item container>
                  <Grid item>{renderIconForRule(ruleType)}</Grid>
                  <Grid>
                    <Typography variant="h6" sx={styles.ml1}>
                      {t(`f-rule-type-${ruleType}`)}
                    </Typography>
                  </Grid>
                </Grid>

                {notificationRules
                  .filter((x) => x.ruleType === ruleType)
                  .map((rule, index) => (
                    <Grid item sm={12} key={rule.id}>
                      <Card elevation={1} sx={styles.mt1} key={index}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignContent: 'center' }}>{renderRuleData(rule)}</Box>
                            <Box>
                              {/* Only offered for the correlation rule, and only when it has a disease: the
                                  review page reads patterns stored per disease, so without one there is nothing
                                  to show. */}
                              {rule.ruleType === RuleType.LungWeatherCorrelation &&
                                rule.ruleData.lungWeatherRuleData?.farmDiseaseId && (
                                  <Tooltip arrow title={t('f-lung-weather-open')}>
                                    <IconButton
                                      id={`lung-weather-${rule.id}`}
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        navigate(`/lung-weather/${rule.ruleData.lungWeatherRuleData?.farmDiseaseId}`)
                                      }
                                      sx={V4_ICON_BUTTON_SMALL}
                                    >
                                      <Icon>air</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}

                              <EmailPreview userIds={rule.emailSettings.userIds} />

                              <Tooltip arrow title={t('f-com-edit')}>
                                <IconButton
                                  id={`upd-${rule.id}`}
                                  size="small"
                                  color="primary"
                                  onClick={() => handleUpdate(rule)}
                                  sx={V4_ICON_BUTTON_SMALL}
                                >
                                  <Icon>{'edit'}</Icon>
                                </IconButton>
                              </Tooltip>

                              <IconButton onClick={() => deleteRule(rule)} sx={V4_ICON_BUTTON}>
                                <DeleteOutline color="error" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Divider />

                          <Box sx={{ mt: 1 }}>{renderPeriodData(rule, formatDate)}</Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            ))}
          </Grid>
        ) : (
          [...Array(notificationRules.length > 0 ? notificationRules.length : size).keys()].map((_, i) => (
            <span key={i}>{renderSkeleton()}</span>
          ))
        )}

        <Grid container direction="row" justifyContent="space-between" sx={styles.mt1} spacing={2}>
          <Grid item xs={12} md={2}>
            <FormControl variant="outlined" size="small" disabled={isLoading} fullWidth>
              <InputLabel>{t('f-size')}</InputLabel>
              <Select
                label={t('f-size')}
                value={size}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setSize(newSize);
                  getNotifications(page, newSize);
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item container xs={12} md={6} sx={styles.pagination}>
            <Pagination
              count={totalPages}
              color="primary"
              disabled={isLoading}
              page={page}
              onChange={(_, page) => getNotifications(page, size)}
            />
          </Grid>
        </Grid>

        <NotificationRuleDialog
          open={notificationRuleDialogOpen}
          setOpen={setNotificationRuleDialogOpen}
          mode={dialogMode}
          rule={selectedRule}
          callback={() => getNotifications(page, size)}
        />
      </Box>
    </>
  );
};

const renderSkeleton = () => (
  <Box sx={{ mt: 2 }}>
    <Card variant="outlined" style={{ height: 170 }}>
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex' }}>
            <Skeleton>
              <Checkbox color="secondary" />
            </Skeleton>
            <Box sx={{ ml: 1 }}>
              <Skeleton width={162}>
                <Checkbox color="secondary" />
              </Skeleton>
            </Box>
          </Box>
          <Skeleton>
            <Checkbox color="secondary" />
            <Checkbox color="secondary" />
            <Checkbox color="secondary" />
          </Skeleton>
        </Box>
        <Skeleton height={40} width="44%" />
        <Skeleton height={40} width="88%" />
      </Box>
    </Card>
  </Box>
);

const renderPeriodData = (rule: NotificationRuleViewModel, formatDate: (dateStr: string) => string) => {
  // The forecast comparison stamps its own timestamp, and it is the only one this rule type moves - showing it is
  // the only way to tell from the UI that the rule is actually being evaluated.
  const forecastCheck =
    rule.ruleType === RuleType.LungWeatherCorrelation ? (
      <Grid item>
        <Typography variant="subtitle2" color="textSecondary">
          {t('f-notif-rule-last-forecast-check')}:{' '}
          {rule.lastForecastMatchedAt ? formatDate(rule.lastForecastMatchedAt.toString()) : '-'}
        </Typography>
      </Grid>
    ) : null;

  switch (rule.validationPeriodType) {
    case ValidationPeriodType.EveryWeek:
      return (
        <Grid container spacing={2} justifyContent="flex-end">
          {forecastCheck}
          <Grid item>
            <Typography variant="subtitle2">{t('f-com-every-week')}</Typography>
          </Grid>
        </Grid>
      );
    case ValidationPeriodType.Weeks:
      const dayOfWeek = rule.periodData?.weeksData?.dayOfWeek;
      return (
        <Grid container spacing={2} justifyContent="flex-end">
          {forecastCheck}
          <Grid item>
            <Typography variant="subtitle2">
              {rule.periodData?.weeksData?.weekCount} {t('f-com-week-s')} - {DayOfWeek[dayOfWeek ? dayOfWeek : 0]}
            </Typography>
          </Grid>
        </Grid>
      );
    case ValidationPeriodType.Daily:
      // No week count or weekday to show: the window comes from the rule's own timescale field.
      return (
        <Grid container spacing={2} justifyContent="flex-end">
          {forecastCheck}
          <Grid item>
            <Typography variant="subtitle2">{t('f-com-daily')}</Typography>
          </Grid>
        </Grid>
      );
  }
};

const renderIconForRule = (ruleType: RuleType) => {
  switch (ruleType) {
    case RuleType.MinRegistered:
      return <Icon>playlist_add_check</Icon>;
    case RuleType.NumLimit:
      return <Icon>equalizer</Icon>;
    case RuleType.PercentageLimit:
      return <Icon>percent</Icon>;
    case RuleType.LungWeatherCorrelation:
      return <Icon>air</Icon>;
  }
};

const renderRuleData = (rule?: NotificationRuleViewModel) => {
  switch (rule?.ruleType) {
    case RuleType.PercentageLimit:
      return renderPercentRuleData(rule.ruleData.percentageRuleData);
    case RuleType.MinRegistered:
      return renderMinRegRuleData(rule.ruleData.minRegAnimalsRuleData);
    case RuleType.NumLimit:
      return renderNumRuleData(rule.ruleData.numRuleData);
    case RuleType.LungWeatherCorrelation:
      return renderLungWeatherRuleData(rule.ruleData.lungWeatherRuleData);
  }
};

const renderLungWeatherRuleData = (data?: LungWeatherRuleDataViewModel) => {
  return (
    data && (
      <Grid container spacing={4} alignItems="flex-end">
        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-animal-type')}
          </Typography>
          <Typography variant="body1">{t(animalTypeTranslations[data.animalType])}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-disease')}
          </Typography>
          <Typography variant="body1">{data.farmDisease?.name ?? '-'}</Typography>
        </Grid>

        {/* No medication column: the medication that counts towards a peak is the one configured on the
            disease, so it is not a property of the rule. */}

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-notif-rule-peak-threshold')}
          </Typography>
          <Typography variant="body1">{data.peakThreshold}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-notif-rule-lookback-days')}
          </Typography>
          <Typography variant="body1">
            {data.lookbackDays} {t('f-day-s')}
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-notif-rule-forecast-days')}
          </Typography>
          <Typography variant="body1">
            {data.forecastDays} {t('f-day-s')}
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-notif-rule-sensitivity')}
          </Typography>
          <Typography variant="body1">
            {data.sensitivity ? t(`f-notif-rule-sensitivity-${data.sensitivity.toLowerCase()}`) : '-'}
          </Typography>
        </Grid>
      </Grid>
    )
  );
};

const animalTypeTranslations: Record<AnimalType, string> = {
  [AnimalType.Unknown]: 'f-any',
  [AnimalType.Sow]: 'm-animal-type-sow',
  [AnimalType.Gilt]: 'm-animal-type-gilt',
  [AnimalType.Piglet]: 'm-animal-type-piglet',
  [AnimalType.Fattening]: 'm-animal-type-fattening',
  [AnimalType.Cow]: 'm-animal-type-cow',
  [AnimalType.Calf]: 'm-animal-type-calf',
  [AnimalType.Goat]: 'm-animal-type-goat',
  [AnimalType.Boar]: 'm-animal-type-boar'
};

const renderMinRegRuleData = (data?: MinRegAnimalsRuleData) => {
  return (
    data && (
      <Grid container spacing={4} alignItems="flex-end">
        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-animal-type')}
          </Typography>
          <Typography variant="body1">{t(animalTypeTranslations[data?.animalType])}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-notif-rule-min-reg-title')}
          </Typography>
          <Typography variant="body1">{data?.minRegistered}</Typography>
        </Grid>
      </Grid>
    )
  );
};

const renderNumRuleData = (data?: NumRuleDataViewModel) => {
  return (
    data && (
      <Grid container spacing={4} alignItems="flex-end">
        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-animal-type')}
          </Typography>
          <Typography variant="body1">{t(animalTypeTranslations[data?.animalType])}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-supply')}
          </Typography>
          <Typography variant="body1">{data?.farmMedicalSupply?.number ?? '-'}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-disease')}
          </Typography>
          <Typography variant="body1">{data?.farmDisease?.name ?? '-'}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-supp-rule-anim-min')}
          </Typography>
          <Typography variant="body1">{data?.minInjected}</Typography>
        </Grid>

        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox checked={data.isPregnant} color="primary" disabled />
            <Typography>{t('f-animal-is-lact')}</Typography>
          </Box>
        </Grid>
        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox checked={data.isLactating} color="primary" disabled />
            <Typography>{t('f-animal-is-preg')}</Typography>
          </Box>
        </Grid>
      </Grid>
    )
  );
};

const renderPercentRuleData = (data?: PercentageRuleData) => {
  return (
    data && (
      <Grid container alignItems="flex-end" spacing={2}>
        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-animal-type')}
          </Typography>
          <Typography variant="body1">{t(animalTypeTranslations[data?.animalType])}</Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-supp-rule-age')}
          </Typography>
          <Typography variant="body1">
            {data?.ageFrom} - {data?.ageTill}
          </Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-supply')}
          </Typography>
          <Typography variant="body1">{data?.farmMedicalSupply?.number ?? '-'}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-com-disease')}
          </Typography>
          <Typography variant="body1">{data?.farmDisease?.name ?? '-'}</Typography>
        </Grid>

        <Grid item>
          <Typography variant="subtitle1" color="primary">
            {t('f-supp-rule-perc')}
          </Typography>
          <Typography variant="body1">{data?.percentage}%</Typography>
        </Grid>

        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 150 }}>
            <Checkbox checked={data.isPregnant} color="primary" disabled />
            <Typography>{t('f-animal-is-preg')}</Typography>
          </Box>
        </Grid>

        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 150 }}>
            <Checkbox checked={data.isLactating} color="primary" disabled />
            <Typography>{t('f-animal-is-lact')}</Typography>
          </Box>
        </Grid>
      </Grid>
    )
  );
};

export default NotificationRulesList;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    mt1: {
      marginTop: theme.spacing(1)
    },
    ml1: {
      marginLeft: theme.spacing(1)
    },
    pagination: {
      '@media (max-width:959.95px)': {
        justifyContent: 'center'
      },
      justifyContent: 'flex-end'
    }
  }) as const;
